import React, { useCallback, useEffect, useState } from 'react';
import { X, UserRound } from 'lucide-react';
import { api } from '../lib/api';

/**
 * AdminUsersListModal
 * -------------------
 * Shown when an admin clicks one of the Users-tab stat cards
 * (Today / 7d / 30d / Range). Displays the list of users matching
 * that period. Clicking a user closes this modal and asks the
 * parent to open the existing AdminUserProfileModal for that user.
 *
 * Props:
 *  - isOpen: bool
 *  - onClose: fn()
 *  - period: 'today' | '7d' | '30d' | 'range'
 *  - title: string (card label e.g. "Today", "Last 7 Days")
 *  - fromDate, toDate: string — only used when period === 'range'
 *  - onSelectUser: fn(userId) — parent opens profile modal
 */
const PERIOD_LABELS = {
  today: 'Today',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  range: 'Selected Range',
};

const AdminUsersListModal = ({
  isOpen,
  onClose,
  period,
  title,
  fromDate,
  toDate,
  onSelectUser,
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const effectiveTitle = title || PERIOD_LABELS[period] || 'Users';

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { period };
      if (period === 'range') {
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
      }
      const { data } = await api.getAdminUsersByPeriod(params);
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [period, fromDate, toDate]);

  useEffect(() => {
    if (!isOpen) return;
    loadUsers();
  }, [isOpen, loadUsers]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="admin-users-list-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />

      <div className="relative w-full max-w-md rounded-t-[20px] sm:rounded-[20px] bg-white shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-[#ececec]">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-[#9333EA] font-semibold font-['DM_Sans']">
              Users Filter
            </p>
            <h3 className="text-base font-bold text-[#111111] font-['Outfit']">
              {effectiveTitle}
            </h3>
            {period === 'range' && (fromDate || toDate) && (
              <p className="text-[11px] text-[#666666] font-['DM_Sans']">
                {fromDate || '—'} &nbsp;→&nbsp; {toDate || '—'}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-users-list-close"
            className="p-1.5 rounded-full hover:bg-[#f4f4f4]"
          >
            <X className="w-4 h-4 text-[#666666]" />
          </button>
        </div>

        <div className="px-3 py-3 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 text-center">
              <div
                className="inline-block w-6 h-6 border-2 border-[#9333EA] border-t-transparent rounded-full animate-spin"
                aria-label="Loading"
              />
              <p className="mt-2 text-xs text-[#666666] font-['DM_Sans']">Loading users...</p>
            </div>
          ) : error ? (
            <p className="py-6 text-xs text-red-600 font-['DM_Sans'] text-center">{error}</p>
          ) : users.length === 0 ? (
            <p className="py-10 text-xs text-[#666666] font-['DM_Sans'] text-center">
              No users found for this period.
            </p>
          ) : (
            <div className="space-y-2">
              <p
                className="text-[11px] text-[#666666] font-['DM_Sans'] px-1"
                data-testid="admin-users-list-count"
              >
                {users.length} user{users.length === 1 ? '' : 's'}
              </p>
              {users.map((u) => {
                const isActive = Boolean(u.isActiveForPeriod);
                return (
                  <button
                    key={u.id}
                    type="button"
                    data-testid={`admin-users-list-row-${u.id}`}
                    onClick={() => {
                      onSelectUser?.(u.id);
                    }}
                    className="w-full rounded-[14px] border border-[#ececec] bg-white px-3 py-2.5 flex items-center gap-3 text-left hover:border-[#9333EA]/40 transition-colors"
                  >
                    {u.profilePic ? (
                      <img
                        src={u.profilePic}
                        alt={u.username}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-[#9333EA]/10 flex items-center justify-center flex-shrink-0">
                        <UserRound className="w-5 h-5 text-[#9333EA]" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#111111] font-['Outfit'] truncate">
                        {u.name || u.username || 'Unknown user'}
                      </p>
                      <p className="text-xs text-[#666666] font-['DM_Sans'] truncate">
                        {u.username}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded-full flex-shrink-0 ${
                        isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-[#f4f4f4] text-[#777777]'
                      }`}
                    >
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersListModal;
